import React, { useState, useEffect } from 'react';
import {
    getBonusRegles, createBonusRegle, updateBonusRegle, deleteBonusRegle,
    getBonusCalculs, calculerBonus, validerBonus, validerBonusMasse,
    getSimulationBonus, getBonusStats, getCommerciaux, getProduits, getZones
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import {
    FiDollarSign, FiPlus, FiCheck, FiPlay, FiDownload, FiSettings, FiTrash2,
    FiEdit2, FiTarget, FiTrendingUp, FiUsers, FiPercent, FiPackage, FiMapPin,
    FiActivity, FiLayers, FiX, FiCheckCircle, FiAlertCircle, FiClock,
    FiFilter, FiCalendar, FiSearch, FiGrid, FiList
} from 'react-icons/fi';

type Tab = 'dashboard' | 'regles' | 'calculs' | 'simulation';

const MODE_LABELS: any = {
    pourcentage_ca: '% du CA', montant_fixe_unite: 'Fixe / unite',
    montant_fixe_global: 'Montant fixe global', paliers: 'Paliers progressifs'
};
const MODE_ICONS: any = {
    pourcentage_ca: <FiPercent size={13} />, montant_fixe_unite: <FiPackage size={13} />,
    montant_fixe_global: <FiDollarSign size={13} />, paliers: <FiLayers size={13} />
};
const TYPE_LABELS: any = { activite: 'Activite', produit: 'Produit', performance: 'Performance', objectif: 'Objectif' };
const TYPE_COLORS: any = { activite: '#16A34A', produit: '#3B82F6', performance: '#F59E0B', objectif: '#8B5CF6' };
const ACTIVITE_LABELS: any = { vente: 'Vente', commande: 'Commande', visite: 'Visite', installation: 'Installation', sensibilisation: 'Sensibilisation', recrutement: 'Recrutement', inscription: 'Inscription' };
const STATUT_COLORS: any = { calcule: '#F59E0B', valide: '#16A34A', paye: '#3B82F6', annule: '#EF4444' };
const PERIODES: any = { jour: 'Jour', semaine: 'Semaine', mois: 'Mois', trimestre: 'Trimestre', annee: 'Annee' };

const n = (v: any) => Math.round(Number(v) || 0);
const fmtCA = (v: any) => n(v).toLocaleString() + ' F';

const Bonus: React.FC = () => {
    const { isAdmin, isManager } = useAuth();
    const [tab, setTab] = useState<Tab>(isAdmin() || isManager() ? 'dashboard' : 'simulation');
    const [regles, setRegles] = useState<any[]>([]);
    const [calculs, setCalculs] = useState<any[]>([]);
    const [calcResume, setCalcResume] = useState<any>(null);
    const [simulation, setSimulation] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [commerciaux, setCommerciaux] = useState<any[]>([]);
    const [produits, setProduits] = useState<any[]>([]);
    const [zones, setZones] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Calculs filters
    const [calcFilters, setCalcFilters] = useState({
        user_id: '', statut: '', regle_id: '',
        periode_debut: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        periode_fin: new Date().toISOString().split('T')[0]
    });
    const [calcVue, setCalcVue] = useState<'detail' | 'jour' | 'semaine' | 'mois' | 'commercial'>('detail');

    // Modal states
    const [showRegleModal, setShowRegleModal] = useState(false);
    const [editRegle, setEditRegle] = useState<any>(null);
    const [showCalcModal, setShowCalcModal] = useState(false);
    const [calcForm, setCalcForm] = useState({ periode_debut: '', periode_fin: '', user_id: '' });

    // Rule form
    const emptyForm = {
        nom: '', description: '', type: 'activite', mode_calcul: 'pourcentage_ca',
        produit_id: '', categorie_id: '', type_activite: '', seuil_min: 0, seuil_max: '',
        taux_commission: '', montant_fixe: '', periode: 'mois',
        applicable_a: 'tous', user_id: '', zone_id: '', cumulable: true,
        paliers: [{ seuil_min: 0, seuil_max: '', taux_commission: '', montant_fixe: '' }]
    };
    const [form, setForm] = useState<any>({ ...emptyForm });

    const loadCalculs = (filters?: any) => {
        const f = filters || calcFilters;
        const params: any = {};
        if (f.user_id) params.user_id = f.user_id;
        if (f.statut) params.statut = f.statut;
        if (f.regle_id) params.regle_id = f.regle_id;
        if (f.periode_debut) params.periode_debut = f.periode_debut;
        if (f.periode_fin) params.periode_fin = f.periode_fin;
        getBonusCalculs(params).then(r => {
            setCalculs(r.data.calculs || r.data);
            setCalcResume(r.data.resume || null);
        }).catch(() => {});
    };

    useEffect(() => {
        Promise.all([
            getBonusRegles().then(r => setRegles(r.data)),
            getCommerciaux().then(r => { const d = r.data.data || r.data; setCommerciaux(Array.isArray(d) ? d : []); }),
            getProduits().then(r => { const d = r.data.data || r.data; setProduits(Array.isArray(d) ? d : []); }).catch(() => {}),
            getZones().then(r => { const d = r.data.data || r.data; setZones(Array.isArray(d) ? d : []); }).catch(() => {}),
        ]).finally(() => setLoading(false));
        if (isAdmin() || isManager()) {
            getBonusStats().then(r => setStats(r.data)).catch(() => {});
            loadCalculs();
        }
        getSimulationBonus().then(r => setSimulation(r.data)).catch(() => {});
    }, []);

    const reload = () => {
        getBonusRegles().then(r => setRegles(r.data));
        loadCalculs();
        getBonusStats().then(r => setStats(r.data)).catch(() => {});
        getSimulationBonus().then(r => setSimulation(r.data)).catch(() => {});
    };

    // Rule modal
    const openNewRegle = () => { setEditRegle(null); setForm({ ...emptyForm }); setShowRegleModal(true); };
    const openEditRegle = (r: any) => {
        setEditRegle(r);
        setForm({
            nom: r.nom, description: r.description || '', type: r.type, mode_calcul: r.mode_calcul || 'pourcentage_ca',
            produit_id: r.produit_id ? String(r.produit_id) : '', categorie_id: r.categorie_id ? String(r.categorie_id) : '',
            type_activite: r.type_activite || '', seuil_min: r.seuil_min || 0, seuil_max: r.seuil_max || '',
            taux_commission: r.taux_commission || '', montant_fixe: r.montant_fixe || '', periode: r.periode || 'mois',
            applicable_a: r.applicable_a || 'tous', user_id: r.user_id ? String(r.user_id) : '',
            zone_id: r.zone_id ? String(r.zone_id) : '', cumulable: r.cumulable !== 0, actif: r.actif,
            paliers: r.paliers?.length > 0 ? r.paliers.map((p: any) => ({
                seuil_min: p.seuil_min, seuil_max: p.seuil_max || '', taux_commission: p.taux_commission || '', montant_fixe: p.montant_fixe || ''
            })) : [{ seuil_min: 0, seuil_max: '', taux_commission: '', montant_fixe: '' }]
        });
        setShowRegleModal(true);
    };

    const handleRegleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const data = {
            ...form,
            produit_id: form.produit_id || null, categorie_id: form.categorie_id || null,
            type_activite: form.type_activite || null, user_id: form.user_id || null, zone_id: form.zone_id || null,
            seuil_max: form.seuil_max || null, taux_commission: form.taux_commission || null,
            montant_fixe: form.montant_fixe || null,
            paliers: form.mode_calcul === 'paliers' ? form.paliers : []
        };
        if (editRegle) await updateBonusRegle(editRegle.id, data);
        else await createBonusRegle(data);
        setShowRegleModal(false);
        reload();
    };

    const handleDeleteRegle = async (id: number) => {
        if (!window.confirm('Supprimer cette regle ?')) return;
        await deleteBonusRegle(id);
        reload();
    };

    const handleToggleRegle = async (r: any) => {
        await updateBonusRegle(r.id, { ...r, actif: r.actif ? 0 : 1 });
        reload();
    };

    const handleCalculer = async (e: React.FormEvent) => {
        e.preventDefault();
        await calculerBonus(calcForm);
        setShowCalcModal(false);
        reload();
    };

    const handleValider = async (id: number) => {
        await validerBonus(id, { statut: 'valide' });
        reload();
    };

    const handleValiderTous = async () => {
        const ids = calculs.filter(c => c.statut === 'calcule').map(c => c.id);
        if (ids.length === 0) return;
        await validerBonusMasse({ ids, statut: 'valide' });
        reload();
    };

    // Palier helpers
    const addPalier = () => setForm({ ...form, paliers: [...form.paliers, { seuil_min: form.paliers.length > 0 ? form.paliers[form.paliers.length - 1].seuil_max || 0 : 0, seuil_max: '', taux_commission: '', montant_fixe: '' }] });
    const removePalier = (i: number) => setForm({ ...form, paliers: form.paliers.filter((_: any, idx: number) => idx !== i) });
    const updatePalier = (i: number, field: string, val: any) => {
        const p = [...form.paliers]; p[i] = { ...p[i], [field]: val }; setForm({ ...form, paliers: p });
    };

    if (loading) return <div className="loading">Chargement...</div>;

    const tabs: { key: Tab; label: string; icon: React.ReactNode; show: boolean }[] = [
        { key: 'dashboard', label: 'Tableau de bord', icon: <FiTrendingUp size={14} />, show: isAdmin() || isManager() },
        { key: 'regles', label: 'Regles & Commissions', icon: <FiSettings size={14} />, show: isAdmin() || isManager() },
        { key: 'calculs', label: 'Calculs', icon: <FiDollarSign size={14} />, show: isAdmin() || isManager() },
        { key: 'simulation', label: 'Ma Simulation', icon: <FiTarget size={14} />, show: true },
    ];

    return (
        <div className="page-bonus">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <h2 style={{ margin: 0 }}><FiDollarSign /> Bonus & Commissions</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    {(isAdmin() || isManager()) && (
                        <>
                            <button className="btn btn-outline" onClick={() => { setCalcForm({ periode_debut: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], periode_fin: new Date().toISOString().split('T')[0], user_id: '' }); setShowCalcModal(true); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <FiPlay size={14} /> Lancer calcul
                            </button>
                            <button className="btn btn-primary" onClick={openNewRegle} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <FiPlus /> Nouvelle regle
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs" style={{ marginBottom: 16 }}>
                {tabs.filter(t => t.show).map(t => (
                    <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* ═══ DASHBOARD ═══ */}
            {tab === 'dashboard' && stats && (
                <div>
                    <div className="bonus-kpi-grid">
                        {[
                            { label: 'Regles actives', value: stats.regles_actives, icon: <FiSettings size={18} />, color: '#8B5CF6', bg: '#f3f0ff' },
                            { label: 'Bonus ce mois', value: fmtCA(stats.total_mois?.total), icon: <FiDollarSign size={18} />, color: '#16A34A', bg: '#dcfce7', sub: `${stats.total_mois?.nb || 0} calculs` },
                            { label: 'En attente validation', value: fmtCA(stats.en_attente?.total), icon: <FiClock size={18} />, color: '#F59E0B', bg: '#fef3c7', sub: `${stats.en_attente?.nb || 0} calculs` },
                            { label: 'Valides ce mois', value: fmtCA(stats.valides?.total), icon: <FiCheckCircle size={18} />, color: '#3B82F6', bg: '#dbeafe', sub: `${stats.valides?.nb || 0} calculs` },
                        ].map((k, i) => (
                            <div key={i} className="bonus-kpi-card" style={{ '--accent': k.color, borderColor: 'transparent' } as any}>
                                <style>{`.bonus-kpi-card:nth-child(${i+1})::before { background: ${k.color}; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: k.bg, color: k.color }}>{k.icon}</div>
                                    <span className="bk-label">{k.label}</span>
                                </div>
                                <div className="bk-value">{k.value}</div>
                                {k.sub && <div className="bk-sub">{k.sub}</div>}
                            </div>
                        ))}
                    </div>

                    {/* Top earners */}
                    {stats.top_earners?.length > 0 && (
                        <div className="bonus-section">
                            <h3 className="bonus-section-title"><FiUsers size={16} /> Top bonus ce mois</h3>
                            {stats.top_earners.map((e: any, i: number) => (
                                <div key={i} className="bonus-earner-row">
                                    <span className={`bonus-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'other'}`}>{i + 1}</span>
                                    <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{e.nom}</span>
                                    <span style={{ fontWeight: 700, color: '#16A34A', fontSize: 14 }}>{fmtCA(e.total)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ REGLES ═══ */}
            {tab === 'regles' && (
                <div>
                    {regles.length === 0 ? (
                        <div className="bonus-empty">
                            <FiSettings size={44} />
                            <p>Aucune regle configuree</p>
                            <button className="btn btn-primary" onClick={openNewRegle}><FiPlus /> Creer une regle</button>
                        </div>
                    ) : (
                        <div className="bonus-regles-grid">
                            {regles.map((r: any) => (
                                <div key={r.id} className={`bonus-regle-card ${r.actif ? '' : 'inactive'}`}>
                                    {/* Header */}
                                    <div className="brc-header">
                                        <div>
                                            <div className="brc-name">{r.nom}</div>
                                            {r.description && <div className="brc-desc">{r.description}</div>}
                                        </div>
                                        <div className="brc-actions">
                                            <button onClick={() => openEditRegle(r)} title="Modifier" style={{ color: '#3B82F6' }}><FiEdit2 size={14} /></button>
                                            <button onClick={() => handleToggleRegle(r)} title={r.actif ? 'Desactiver' : 'Activer'} style={{ color: r.actif ? '#16A34A' : '#999' }}>{r.actif ? <FiCheckCircle size={14} /> : <FiAlertCircle size={14} />}</button>
                                            <button onClick={() => handleDeleteRegle(r.id)} title="Supprimer" style={{ color: '#EF4444' }}><FiTrash2 size={14} /></button>
                                        </div>
                                    </div>

                                    {/* Badges */}
                                    <div className="bonus-badges">
                                        <span className="bonus-badge" style={{ background: `${TYPE_COLORS[r.type]}15`, color: TYPE_COLORS[r.type] }}>{TYPE_LABELS[r.type]}</span>
                                        <span className="bonus-badge mode">{MODE_ICONS[r.mode_calcul]} {MODE_LABELS[r.mode_calcul] || r.mode_calcul}</span>
                                        <span className="bonus-badge periode">{PERIODES[r.periode]}</span>
                                        {r.type_activite && <span className="bonus-badge activite">{ACTIVITE_LABELS[r.type_activite]}</span>}
                                    </div>

                                    {/* Details grid */}
                                    <div className="bonus-details-grid">
                                        {r.mode_calcul === 'pourcentage_ca' && r.taux_commission && (
                                            <div className="bd-item"><span className="bd-label">Commission:</span> <strong className="bd-value accent">{r.taux_commission}%</strong></div>
                                        )}
                                        {(r.mode_calcul === 'montant_fixe_unite' || r.mode_calcul === 'montant_fixe_global') && r.montant_fixe && (
                                            <div className="bd-item"><span className="bd-label">Montant fixe:</span> <strong className="bd-value">{fmtCA(r.montant_fixe)}</strong></div>
                                        )}
                                        {r.seuil_min > 0 && <div className="bd-item"><span className="bd-label">Seuil min:</span> <strong className="bd-value">{n(r.seuil_min).toLocaleString()}</strong></div>}
                                        {r.seuil_max && <div className="bd-item"><span className="bd-label">Seuil max:</span> <strong className="bd-value">{n(r.seuil_max).toLocaleString()}</strong></div>}
                                        {r.produit_nom && <div className="bd-item"><span className="bd-label">Produit:</span> <strong className="bd-value">{r.produit_nom}</strong></div>}
                                        {r.categorie_nom && <div className="bd-item"><span className="bd-label">Categorie:</span> <strong className="bd-value">{r.categorie_nom}</strong></div>}
                                    </div>

                                    {/* Applicability */}
                                    <div className="bonus-applicability">
                                        {r.applicable_a === 'tous' && <><FiUsers size={12} /> Tous les commerciaux</>}
                                        {r.applicable_a === 'commercial' && <><FiActivity size={12} /> {r.commercial_nom || 'Commercial specifique'}</>}
                                        {r.applicable_a === 'zone' && <><FiMapPin size={12} /> Zone: {r.zone_nom || 'Specifique'}</>}
                                        {!r.cumulable && <span style={{ marginLeft: 'auto', color: '#F59E0B', fontWeight: 600 }}>Non cumulable</span>}
                                    </div>

                                    {/* Paliers */}
                                    {r.paliers?.length > 0 && (
                                        <div className="bonus-paliers">
                                            <div className="bonus-paliers-title">Paliers</div>
                                            {r.paliers.map((p: any, i: number) => (
                                                <div key={i} className="bonus-palier-row">
                                                    <span className="bp-range">{n(p.seuil_min).toLocaleString()}{p.seuil_max ? ` → ${n(p.seuil_max).toLocaleString()}` : '+'}</span>
                                                    <span className="bp-value">{p.taux_commission ? `${p.taux_commission}%` : fmtCA(p.montant_fixe)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ CALCULS ═══ */}
            {tab === 'calculs' && (
                <div>
                    {/* Filters */}
                    <div className="bonus-section" style={{ padding: 16, marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <FiFilter size={14} color="var(--primary)" />
                            <span style={{ fontWeight: 600, fontSize: 13 }}>Filtres</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                            <div>
                                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 4 }}>Periode debut</label>
                                <input type="date" value={calcFilters.periode_debut}
                                    onChange={e => setCalcFilters({ ...calcFilters, periode_debut: e.target.value })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 4 }}>Periode fin</label>
                                <input type="date" value={calcFilters.periode_fin}
                                    onChange={e => setCalcFilters({ ...calcFilters, periode_fin: e.target.value })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 4 }}>Commercial</label>
                                <SearchableSelect
                                    value={calcFilters.user_id}
                                    onChange={(v: string) => setCalcFilters({ ...calcFilters, user_id: v })}
                                    placeholder="Tous"
                                    options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 4 }}>Statut</label>
                                <select value={calcFilters.statut} onChange={e => setCalcFilters({ ...calcFilters, statut: e.target.value })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }}>
                                    <option value="">Tous</option>
                                    <option value="calcule">Calcule</option>
                                    <option value="valide">Valide</option>
                                    <option value="paye">Paye</option>
                                    <option value="annule">Annule</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 4 }}>Regle</label>
                                <select value={calcFilters.regle_id} onChange={e => setCalcFilters({ ...calcFilters, regle_id: e.target.value })}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13 }}>
                                    <option value="">Toutes</option>
                                    {regles.map((r: any) => <option key={r.id} value={r.id}>{r.nom}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <button className="btn btn-primary" onClick={() => loadCalculs(calcFilters)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', justifyContent: 'center' }}>
                                    <FiSearch size={14} /> Rechercher
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Resume KPIs */}
                    {calcResume && (
                        <div className="bonus-kpi-grid" style={{ marginBottom: 16 }}>
                            <div className="bonus-kpi-card">
                                <style>{`.bonus-kpi-card:nth-child(1)::before { background: #16A34A; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: '#dcfce7', color: '#16A34A' }}><FiDollarSign size={18} /></div>
                                    <span className="bk-label">Total bonus</span>
                                </div>
                                <div className="bk-value">{fmtCA(calcResume.total)}</div>
                                <div className="bk-sub">{calcResume.nb} calcul(s)</div>
                            </div>
                            <div className="bonus-kpi-card">
                                <style>{`.bonus-kpi-card:nth-child(2)::before { background: #F59E0B; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: '#fef3c7', color: '#F59E0B' }}><FiClock size={18} /></div>
                                    <span className="bk-label">En attente</span>
                                </div>
                                <div className="bk-value">{calcResume.calcule}</div>
                            </div>
                            <div className="bonus-kpi-card">
                                <style>{`.bonus-kpi-card:nth-child(3)::before { background: #3B82F6; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: '#dbeafe', color: '#3B82F6' }}><FiCheckCircle size={18} /></div>
                                    <span className="bk-label">Valides</span>
                                </div>
                                <div className="bk-value">{calcResume.valide}</div>
                            </div>
                            <div className="bonus-kpi-card">
                                <style>{`.bonus-kpi-card:nth-child(4)::before { background: #8B5CF6; }`}</style>
                                <div className="bk-header">
                                    <div className="bk-icon" style={{ background: '#f3f0ff', color: '#8B5CF6' }}><FiActivity size={18} /></div>
                                    <span className="bk-label">Payes</span>
                                </div>
                                <div className="bk-value">{calcResume.paye}</div>
                            </div>
                        </div>
                    )}

                    {/* View mode toggle + actions */}
                    <div className="bonus-calculs-header" style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', gap: 4, background: 'var(--divider)', borderRadius: 8, padding: 3 }}>
                            {([
                                { key: 'detail', label: 'Detail', icon: <FiList size={13} /> },
                                { key: 'jour', label: 'Jour', icon: <FiCalendar size={13} /> },
                                { key: 'semaine', label: 'Semaine', icon: <FiCalendar size={13} /> },
                                { key: 'mois', label: 'Mois', icon: <FiCalendar size={13} /> },
                                { key: 'commercial', label: 'Commercial', icon: <FiUsers size={13} /> },
                            ] as { key: typeof calcVue; label: string; icon: React.ReactNode }[]).map(v => (
                                <button key={v.key} onClick={() => setCalcVue(v.key)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
                                        borderRadius: 6, fontSize: 12, fontWeight: calcVue === v.key ? 600 : 400,
                                        border: 'none', cursor: 'pointer',
                                        background: calcVue === v.key ? '#fff' : 'transparent',
                                        color: calcVue === v.key ? 'var(--primary)' : 'var(--text-muted)',
                                        boxShadow: calcVue === v.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                    {v.icon} {v.label}
                                </button>
                            ))}
                        </div>
                        {calculs.filter(c => c.statut === 'calcule').length > 0 && (
                            <button className="btn btn-primary" onClick={handleValiderTous} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <FiCheck /> Valider tous ({calculs.filter(c => c.statut === 'calcule').length})
                            </button>
                        )}
                    </div>

                    {/* Grouped views: Jour / Semaine / Mois / Commercial */}
                    {calcVue !== 'detail' && calcResume && (
                        <div className="bonus-section">
                            <h3 className="bonus-section-title">
                                {calcVue === 'jour' && <><FiCalendar size={15} /> Cumul journalier</>}
                                {calcVue === 'semaine' && <><FiCalendar size={15} /> Cumul hebdomadaire</>}
                                {calcVue === 'mois' && <><FiCalendar size={15} /> Cumul mensuel</>}
                                {calcVue === 'commercial' && <><FiUsers size={15} /> Par commercial</>}
                            </h3>
                            <div className="table-container" style={{ border: 'none', boxShadow: 'none' }}>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{calcVue === 'commercial' ? 'Commercial' : 'Periode'}</th>
                                            {calcVue === 'commercial' && <th>Zone</th>}
                                            <th>Nb calculs</th>
                                            <th>Total bonus</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            let data: any[] = [];
                                            if (calcVue === 'jour') {
                                                data = Object.entries(calcResume.par_jour || {}).sort(([a],[b]) => b.localeCompare(a))
                                                    .map(([k, v]: any) => ({ label: new Date(k + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }), ...v }));
                                            } else if (calcVue === 'semaine') {
                                                data = Object.entries(calcResume.par_semaine || {}).sort(([a],[b]) => b.localeCompare(a))
                                                    .map(([k, v]: any) => ({ label: k, ...v }));
                                            } else if (calcVue === 'mois') {
                                                const MOIS_NOMS = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
                                                data = Object.entries(calcResume.par_mois || {}).sort(([a],[b]) => b.localeCompare(a))
                                                    .map(([k, v]: any) => { const [y, m] = k.split('-'); return { label: `${MOIS_NOMS[parseInt(m)-1]} ${y}`, ...v }; });
                                            } else if (calcVue === 'commercial') {
                                                data = Object.entries(calcResume.par_commercial || {}).sort(([,a]: any,[,b]: any) => b.total - a.total)
                                                    .map(([, v]: any) => ({ label: v.nom, zone: v.zone, matricule: v.matricule, ...v }));
                                            }
                                            return data.length === 0 ? (
                                                <tr><td colSpan={calcVue === 'commercial' ? 4 : 3} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune donnee</td></tr>
                                            ) : data.map((d: any, i: number) => (
                                                <tr key={i}>
                                                    <td>
                                                        <strong>{d.label}</strong>
                                                        {d.matricule && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.matricule}</div>}
                                                    </td>
                                                    {calcVue === 'commercial' && <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.zone || '-'}</td>}
                                                    <td>{d.nb}</td>
                                                    <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{fmtCA(d.total)}</td>
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                    {(() => {
                                        let dataMap: any = calcVue === 'jour' ? calcResume.par_jour : calcVue === 'semaine' ? calcResume.par_semaine : calcVue === 'mois' ? calcResume.par_mois : calcResume.par_commercial;
                                        const entries = Object.values(dataMap || {}) as any[];
                                        const totalNb = entries.reduce((s: number, v: any) => s + v.nb, 0);
                                        const totalAmt = entries.reduce((s: number, v: any) => s + v.total, 0);
                                        return entries.length > 0 ? (
                                            <tfoot>
                                                <tr style={{ fontWeight: 700, background: 'var(--bg)' }}>
                                                    <td>{calcVue === 'commercial' ? 'TOTAL' : 'CUMUL'}</td>
                                                    {calcVue === 'commercial' && <td></td>}
                                                    <td>{totalNb}</td>
                                                    <td style={{ color: 'var(--primary)' }}>{fmtCA(totalAmt)}</td>
                                                </tr>
                                            </tfoot>
                                        ) : null;
                                    })()}
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Detail table */}
                    {calcVue === 'detail' && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Commercial</th><th>Regle</th><th>Periode</th><th>Base</th><th>Bonus</th><th>Statut</th><th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {calculs.length === 0 ? (
                                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucun calcul. Lancez un calcul de bonus.</td></tr>
                                    ) : calculs.map((c: any) => (
                                        <tr key={c.id}>
                                            <td><strong>{c.user_nom}</strong><div style={{ fontSize: 11, color: '#888' }}>{c.matricule}</div></td>
                                            <td>{c.regle_nom}<div style={{ fontSize: 11, color: '#888' }}>{c.mode_calcul ? MODE_LABELS[c.mode_calcul] : c.regle_type}</div></td>
                                            <td style={{ fontSize: 12 }}>{c.periode_debut ? new Date(c.periode_debut).toLocaleDateString('fr') : ''} - {c.periode_fin ? new Date(c.periode_fin).toLocaleDateString('fr') : ''}</td>
                                            <td>{n(c.base_calcul).toLocaleString()}</td>
                                            <td style={{ fontWeight: 700, color: '#16A34A' }}>{fmtCA(c.montant_bonus)}</td>
                                            <td><span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${STATUT_COLORS[c.statut]}15`, color: STATUT_COLORS[c.statut] }}>{c.statut}</span></td>
                                            <td>
                                                {c.statut === 'calcule' && (
                                                    <button className="btn-icon" onClick={() => handleValider(c.id)} title="Valider"><FiCheck color="#16A34A" /></button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ SIMULATION ═══ */}
            {tab === 'simulation' && simulation && (
                <div>
                    <div className="bonus-sim-total">
                        <div className="bst-label">Bonus estime ce mois</div>
                        <div className="bst-value">{fmtCA(simulation.total_estime)}</div>
                        <div className="bst-sub">{simulation.simulations?.length || 0} regle(s) applicable(s)</div>
                    </div>
                    <div style={{ display: 'grid', gap: 14 }}>
                        {(simulation.simulations || []).map((s: any, i: number) => (
                            <div key={i} className="bonus-sim-card">
                                <div className={`bonus-sim-icon ${s.atteint ? 'reached' : 'pending'}`}>
                                    {s.atteint ? <FiCheckCircle size={22} /> : <FiTarget size={22} />}
                                </div>
                                <div className="bonus-sim-info">
                                    <div className="bsi-name">{s.regle}</div>
                                    <div className="bsi-meta">
                                        <span>Base: {n(s.base).toLocaleString()}</span>
                                        <span>Seuil: {n(s.seuil_min).toLocaleString()}</span>
                                        <span>{MODE_LABELS[s.mode_calcul] || s.type}</span>
                                    </div>
                                    <div className="bonus-sim-progress">
                                        <div className={`bar ${s.atteint ? 'reached' : 'pending'}`} style={{ width: `${Math.min(s.progression, 100)}%` }} />
                                    </div>
                                </div>
                                <div className="bonus-sim-amount">
                                    <div className={`bsa-value ${s.montant_estime > 0 ? 'active' : 'zero'}`}>{fmtCA(s.montant_estime)}</div>
                                    <div className="bsa-pct">{s.progression}%</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ═══ RULE MODAL ═══ */}
            {showRegleModal && (
                <div className="modal-overlay" onClick={() => setShowRegleModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3>{editRegle ? 'Modifier la regle' : 'Nouvelle regle de commission'}</h3>
                        <form onSubmit={handleRegleSave}>
                            {/* Basic info */}
                            <div className="form-group"><label>Nom *</label><input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required placeholder="Ex: Commission ventes Aliments" /></div>
                            <div className="form-group"><label>Description</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description optionnelle" /></div>

                            {/* Type + Mode */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Type de regle</label>
                                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                        <option value="activite">Activite</option>
                                        <option value="produit">Produit</option>
                                        <option value="performance">Performance</option>
                                        <option value="objectif">Objectif</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Mode de calcul</label>
                                    <select value={form.mode_calcul} onChange={e => setForm({ ...form, mode_calcul: e.target.value })}>
                                        <option value="pourcentage_ca">% du chiffre d'affaires</option>
                                        <option value="montant_fixe_unite">Montant fixe par unite</option>
                                        <option value="montant_fixe_global">Montant fixe global</option>
                                        <option value="paliers">Paliers progressifs</option>
                                    </select>
                                </div>
                            </div>

                            {/* Activity + Product filters */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Type d'activite</label>
                                    <select value={form.type_activite} onChange={e => setForm({ ...form, type_activite: e.target.value })}>
                                        <option value="">Toutes</option>
                                        {Object.entries(ACTIVITE_LABELS).map(([k, v]) => <option key={k} value={k}>{v as string}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Periode</label>
                                    <select value={form.periode} onChange={e => setForm({ ...form, periode: e.target.value })}>
                                        {Object.entries(PERIODES).map(([k, v]) => <option key={k} value={k}>{v as string}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Product / Category */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Produit specifique</label>
                                    <select value={form.produit_id} onChange={e => setForm({ ...form, produit_id: e.target.value })}>
                                        <option value="">Tous les produits</option>
                                        {produits.map((p: any) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Categorie produit</label>
                                    <select value={form.categorie_id} onChange={e => setForm({ ...form, categorie_id: e.target.value })}>
                                        <option value="">Toutes</option>
                                        {Array.from(new Map(produits.map((p: any) => [p.categorie_id, { id: p.categorie_id, nom: p.categorie_nom || 'Cat ' + p.categorie_id }])).values()).map((c: any) => (
                                            <option key={c.id} value={c.id}>{c.nom}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Thresholds + Values */}
                            {form.mode_calcul !== 'paliers' && (
                                <div className="form-row">
                                    <div className="form-group"><label>Seuil minimum</label><input type="number" value={form.seuil_min} onChange={e => setForm({ ...form, seuil_min: e.target.value })} /></div>
                                    <div className="form-group"><label>Seuil maximum</label><input type="number" value={form.seuil_max} onChange={e => setForm({ ...form, seuil_max: e.target.value })} placeholder="Illimite" /></div>
                                </div>
                            )}
                            {form.mode_calcul === 'pourcentage_ca' && (
                                <div className="form-group"><label>Taux de commission (%)</label><input type="number" step="0.01" value={form.taux_commission} onChange={e => setForm({ ...form, taux_commission: e.target.value })} placeholder="Ex: 2.5" /></div>
                            )}
                            {(form.mode_calcul === 'montant_fixe_unite' || form.mode_calcul === 'montant_fixe_global') && (
                                <div className="form-group"><label>Montant fixe (F)</label><input type="number" value={form.montant_fixe} onChange={e => setForm({ ...form, montant_fixe: e.target.value })} placeholder="Ex: 5000" /></div>
                            )}

                            {/* Paliers */}
                            {form.mode_calcul === 'paliers' && (
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Paliers de commission</label>
                                    <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                                        {form.paliers.map((p: any, i: number) => (
                                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B', width: 20 }}>{i + 1}</span>
                                                <input type="number" placeholder="Min" value={p.seuil_min} onChange={e => updatePalier(i, 'seuil_min', e.target.value)} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                                <input type="number" placeholder="Max" value={p.seuil_max} onChange={e => updatePalier(i, 'seuil_max', e.target.value)} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                                <input type="number" step="0.01" placeholder="%" value={p.taux_commission} onChange={e => updatePalier(i, 'taux_commission', e.target.value)} style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                                <input type="number" placeholder="Fixe (F)" value={p.montant_fixe} onChange={e => updatePalier(i, 'montant_fixe', e.target.value)} style={{ width: 90, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                                {form.paliers.length > 1 && (
                                                    <button type="button" onClick={() => removePalier(i)} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: '#EF4444' }}><FiX size={14} /></button>
                                                )}
                                            </div>
                                        ))}
                                        <button type="button" onClick={addPalier} style={{ fontSize: 12, color: '#3B82F6', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <FiPlus size={12} /> Ajouter un palier
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Applicability */}
                            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Applicable a</label>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                    {[
                                        { val: 'tous', label: 'Tous les commerciaux', icon: <FiUsers size={13} /> },
                                        { val: 'commercial', label: 'Commercial specifique', icon: <FiActivity size={13} /> },
                                        { val: 'zone', label: 'Zone geographique', icon: <FiMapPin size={13} /> },
                                    ].map(opt => (
                                        <button type="button" key={opt.val}
                                            onClick={() => setForm({ ...form, applicable_a: opt.val, user_id: '', zone_id: '' })}
                                            style={{
                                                flex: 1, padding: '8px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                                                border: form.applicable_a === opt.val ? '2px solid #16A34A' : '1px solid #ddd',
                                                background: form.applicable_a === opt.val ? '#f0fdf4' : '#fff',
                                                color: form.applicable_a === opt.val ? '#16A34A' : '#666',
                                                fontWeight: form.applicable_a === opt.val ? 600 : 400,
                                                display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center'
                                            }}>
                                            {opt.icon} {opt.label}
                                        </button>
                                    ))}
                                </div>
                                {form.applicable_a === 'commercial' && (
                                    <SearchableSelect
                                        value={form.user_id}
                                        onChange={(v: string) => setForm({ ...form, user_id: v })}
                                        placeholder="Selectionner un commercial"
                                        options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))}
                                    />
                                )}
                                {form.applicable_a === 'zone' && (
                                    <select value={form.zone_id} onChange={e => setForm({ ...form, zone_id: e.target.value })}
                                        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                                        <option value="">Selectionner une zone</option>
                                        {zones.map((z: any) => <option key={z.id} value={z.id}>{z.nom}</option>)}
                                    </select>
                                )}
                            </div>

                            {/* Cumulable */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 14 }}>
                                <input type="checkbox" checked={form.cumulable} onChange={e => setForm({ ...form, cumulable: e.target.checked })} />
                                Cumulable avec d'autres regles
                            </label>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRegleModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editRegle ? 'Modifier' : 'Creer la regle'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═══ CALC MODAL ═══ */}
            {showCalcModal && (
                <div className="modal-overlay" onClick={() => setShowCalcModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <h3><FiPlay style={{ marginRight: 6 }} /> Lancer le calcul des bonus</h3>
                        <form onSubmit={handleCalculer}>
                            <div className="form-row">
                                <div className="form-group"><label>Debut periode *</label><input type="date" value={calcForm.periode_debut} onChange={e => setCalcForm({ ...calcForm, periode_debut: e.target.value })} required /></div>
                                <div className="form-group"><label>Fin periode *</label><input type="date" value={calcForm.periode_fin} onChange={e => setCalcForm({ ...calcForm, periode_fin: e.target.value })} required /></div>
                            </div>
                            <div className="form-group">
                                <label>Commercial (optionnel)</label>
                                <SearchableSelect
                                    value={calcForm.user_id}
                                    onChange={(v: string) => setCalcForm({ ...calcForm, user_id: v })}
                                    placeholder="Tous les commerciaux"
                                    options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || '' }))}
                                />
                            </div>
                            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Les calculs existants non valides pour cette periode seront recalcules.</p>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCalcModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary"><FiPlay style={{ marginRight: 4 }} /> Calculer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Bonus;
